import * as React from 'react';
import { StatChangeStatus, StatisticType, StatisticUnit } from '../../types';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { antGrey } from '../styles/globalStyles';
import {
    colorForChangeStatArrow,
    displayStatValue,
    isStatTrendValueSignificant,
    statusChangeForStatType,
} from '../../helpers';
import Text from 'antd/lib/typography/Text';
import { isNil } from 'lodash';

interface ChangeValueProps {
    change: number | React.ReactNode;
    changeStatus: StatChangeStatus;
    unit: StatisticUnit;
    type: StatisticType;
}

export const ChangeValue: React.FC<ChangeValueProps> = (props: ChangeValueProps) => {
    const { changeStatus, change, unit, type } = props;
    //if not number, display as string
    const hasChange = typeof change === 'number';
    const changeArrow = hasChange ? arrowForChange(change, changeStatus) : null;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            {changeArrow}
            {change !== 0 ?
                <Text
                    style={{ color: `${hasChange ? antGrey.grey8 : antGrey.grey7}`, fontSize: '24px', fontWeight: 600 }}
                >
                    {hasChange ? displayStatValue(type, change, unit) : change}
                </Text>
            :   <Text style={{ color: antGrey.grey8, fontSize: 18, fontWeight: 200 }}>No change</Text>}
        </div>
    );
};

const arrowForChange: (change: number, changeStatus: StatChangeStatus) => React.ReactNode = (change, changeStatus) => {
    let node: React.ReactNode;
    const circleSize = '24px';
    const arrowSize = '18px';
    const color = colorForChangeStatArrow(changeStatus);

    if (change < 0) {
        node = (
            <div
                style={{
                    height: circleSize,
                    width: circleSize,
                    backgroundColor: color,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <ArrowDownOutlined style={{ fontSize: arrowSize, color: '#fff' }} />
            </div>
        );
    } else if (change > 0) {
        node = (
            <div
                style={{
                    height: circleSize,
                    width: circleSize,
                    backgroundColor: color,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <ArrowUpOutlined style={{ fontSize: arrowSize, color: '#fff' }} />
            </div>
        );
    } else {
        node = <></>;
    }

    return node;
};

interface TrendValueProps {
    trend?: number;
    type: StatisticType;
    unit: StatisticUnit;
    secondaryTrend?: number;
}

export const TrendValue: React.FC<TrendValueProps> = (props: TrendValueProps) => {
    const { unit, type, trend, secondaryTrend } = props;

    const statusChange = statusChangeForStatType({ value: trend, type });
    const changeArrow = trend ? arrowForChange(trend, statusChange) : null;
    const secondaryStatusChange = statusChangeForStatType({ value: trend, type });
    const secondaryChangeArrow = trend ? arrowForChange(trend, secondaryStatusChange) : null;

    const isSignificant = isStatTrendValueSignificant(trend, type);
    const textStyle = {
        color: `${!isNil(trend) && isSignificant ? antGrey.grey8 : antGrey.grey7}`,
        fontSize: 24,
        fontWeight: 600,
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            {!isNil(trend) ?
                <>
                    {isSignificant && changeArrow}
                    <Text style={textStyle}>
                        {isSignificant ?
                            displayStatValue(type, trend, unit)
                        :   <span style={{ fontSize: 18, fontWeight: 200 }}>No significant trend</span>}
                        {!isNil(secondaryTrend) && isSignificant ? ' / ' : null}
                    </Text>
                    {!isNil(secondaryTrend) && isSignificant && (
                        <>
                            {secondaryChangeArrow}
                            <Text style={textStyle}>{displayStatValue(type, secondaryTrend, unit)}</Text>
                        </>
                    )}
                </>
            :   <Text style={{ color: antGrey.grey6, fontSize: 18, fontWeight: 200 }}>--</Text>}
        </div>
    );
};
